/**
 * useSavePortfolio — the Save button: check, send, tell the user.
 *
 *   const { save, saving } = useSavePortfolio({ mode, provider, model, headroom,
 *                                               discount, policyEngine });
 *   const savedName = await save({ name, rows, selfPerfRows, portfolioId, owner,
 *                                  existingPortfolios });
 *
 * `mode` ('cca' | 'eia', default: the running product) picks the body
 * (api/saveApi buildSaveBody): CCA sends `policy_engine` (`policyEngine` = the
 * opened portfolio's saved value; empty -> the default), EIA sends `headroom`
 * and `udf`. `discount` is the UI value { onDemand, oneYearRi, threeYearRi },
 * sent as pricing_model_discount. `provider` is a cloud — in Billing mode the Billing "Cloud" pick.
 *
 * Legacy FooterActions.vue SavePortfolio / SaveAPICall / updateAPICall:
 *  1. the name must be 3–100 characters of letters, numbers, _ and - (legacy's
 *     own message on failure);
 *  2. the name must not be one this user already has on ANOTHER portfolio
 *     ("Portfolio X already exists."). Legacy checks only new portfolios, so
 *     renaming an opened one onto a taken name went through — checked here;
 *  3. new -> POST /save-portfolio; one that already exists on the server (an
 *     opened portfolio, or one an upload created) -> PATCH /update-portfolio?id=.
 * `user_email` is the portfolio's OWNER when one is open (legacy `createdBy`):
 * an Org portfolio stays filed under whoever made it. Otherwise the signed-in
 * user.
 * Toasts as legacy: "Portfolio X added successfully" / "… updated
 * successfully"; a failure shows the server's message (legacy shows nothing
 * for a 200 ErrorCode -1 — here it is said).
 */
import { useState } from 'react';
import { useSelector } from 'react-redux';

import { validatePortfolioName } from '@components/eia/portfolioFormRules';
import { portfolioName as nameOf } from '@features/dashboard';
import { useNotify } from '@hooks/useNotify';
import { selectUser } from '@security/auth/authSlice';
import { resolveAppMode } from '@variant/appMode';

import {
  buildSaveBody,
  readSaveResult,
  useSavePortfolioMutation,
  useUpdatePortfolioMutation,
} from '../api/saveApi';

/** Legacy's message for a bad name (FooterActions.vue :200 / :226). */
export const NAME_RULE_MESSAGE =
  'Please enter a portfolio name between 3 and 100 characters. Only letters, numbers, underscores (_), and hyphens (-) are allowed; no other special characters.';

export function useSavePortfolio({
  mode = resolveAppMode(),
  provider,
  model,
  headroom,
  discount,
  policyEngine,
}) {
  const [saveNew] = useSavePortfolioMutation();
  const [updateExisting] = useUpdatePortfolioMutation();
  const notify = useNotify();
  const email = useSelector(selectUser)?.email ?? '';
  const [saving, setSaving] = useState(false);

  /**
   * @param {{ name: string, rows: object[], selfPerfRows: object[],
   *           portfolioId?: string|null, owner?: string,
   *           existingPortfolios?: object[] }} input
   * @returns {Promise<string|null>} the saved name, or null when not saved
   */
  const save = async ({
    name,
    rows,
    selfPerfRows,
    portfolioId,
    owner,
    existingPortfolios = [],
  }) => {
    // As typed — legacy never trims; a name with a space fails the rule below.
    const typedName = String(name ?? '');

    // 1. The name.
    if (validatePortfolioName(typedName)) {
      notify.error(NAME_RULE_MESSAGE);
      return null;
    }

    // 2. Not a name this user already has on another portfolio.
    const isNew = !portfolioId;
    const taken = existingPortfolios.some(
      (p) => nameOf(p) === typedName && p?.user === email && (isNew || p?._id !== portfolioId),
    );
    if (taken) {
      notify.error(`Portfolio ${typedName} already exists.`);
      return null;
    }

    // 3. Send it.
    const body = buildSaveBody({
      mode,
      name: typedName,
      rows,
      selfPerfRows,
      provider,
      owner,
      email,
      model,
      discount,
      headroom,
      policyEngine,
      portfolioId: isNew ? null : portfolioId,
    });

    setSaving(true);
    try {
      readSaveResult(await (isNew ? saveNew(body) : updateExisting(body)).unwrap());
      notify.success(`Portfolio ${typedName} ${isNew ? 'added' : 'updated'} successfully`);
      return typedName;
    } catch (error) {
      notify.error(error, 'An error occurred while saving the portfolio.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  return { save, saving };
}
